import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Injector,
    computed,
    inject,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
    ActionSheetController,
    AlertController,
    IonApp,
    IonBadge,
    IonContent,
    IonHeader,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonMenu,
    IonRouterOutlet,
    IonSpinner,
    IonTitle,
    IonToolbar,
    LoadingController,
    MenuController,
    NavController,
    Platform,
    ToastController,
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
    add,
    calculatorOutline,
    chevronDownOutline,
    close,
    documentOutline,
    helpCircleOutline,
    layersOutline,
    pencilOutline,
    refresh,
    removeCircle,
    settingsOutline,
    warning,
} from 'ionicons/icons';
import { filter, firstValueFrom } from 'rxjs';
import { BrowserNullParamDefProvider } from './browser-null-param-def-provider';
import { gitHash } from './git-hash';
import packageInfo from '../../package.json';
import { LogFile } from '../hgm-setup/src/module/param/core/logging-types';
import { ParamKeyedTranslator } from '../hgm-setup/src/module/param/services/param-keyed-translator';
import { ParamSystemDefProvider } from '../hgm-setup/src/module/param/util/param-system-def';
import { BrowserLogFileReader } from '../hgm-setup/src/module/util/browser-log-file-reader';
import { safeErrorString, truthyOnly } from '../hgm-setup/src/module/util/core';
import { electronBridge } from '../hgm-setup/src/module/util/electron-bridge';
import { FileIo } from '../hgm-setup/src/module/util/file-io';
import { LogFileProvider } from '../hgm-setup/src/module/util/log-file-provider';
import { LOGGER } from '../hgm-setup/src/module/util/logger';
import { TranslationManager } from '../hgm-setup/src/module/util/translation-manager';
import { ChartConfigurationProvider } from '../providers/chart-configuration-provider';
import { DataProvider } from '../providers/data-provider';
import { GlobalSettingsProvider } from '../providers/global-settings-provider';
import { LogFileOpenRequester } from '../providers/log-file-open-requester';
import { SignalGroupProvider } from '../providers/signal-group-provider';
import { ThemeManager } from '../providers/theme-manager';
import { UpdateProvider } from '../providers/update-provider';

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        IonApp,
        IonBadge,
        IonMenu,
        IonHeader,
        IonToolbar,
        IonTitle,
        IonContent,
        IonList,
        IonItem,
        IonLabel,
        IonIcon,
        IonRouterOutlet,
        IonSpinner,
    ],
})
export class AppComponent {
    isLoading = false;
    fileInputAccept: string;
    fileInput = viewChild.required<ElementRef<HTMLInputElement>>('fileInput');
    configFileInput = viewChild.required<ElementRef<HTMLInputElement>>('configFileInput');
    readonly shortcutPrefix = /Mac|iPhone|iPad/.test(navigator.userAgent) ? '⌘' : 'Ctrl+';
    readonly isLoaded = computed(() => !!this.dataProvider.data());

    private readonly logger = inject(LOGGER);
    private readonly loadingController = inject(LoadingController);
    private readonly toastController = inject(ToastController);
    private readonly logReader = inject(BrowserLogFileReader);
    private readonly dataProvider = inject(DataProvider);
    private readonly injector = inject(Injector);
    private readonly logFileProviderInstance = inject(LogFileProvider);
    private readonly navCtrl = inject(NavController);
    private readonly menuCtrl = inject(MenuController);
    private readonly actionSheetCtrl = inject(ActionSheetController);
    private readonly alertCtrlr = inject(AlertController);
    private readonly updateProvider = inject(UpdateProvider);
    private readonly chartConfigProvider = inject(ChartConfigurationProvider);
    private readonly fileIo = inject(FileIo);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly signalGroupProvider = inject(SignalGroupProvider);
    private readonly openRequester = inject(LogFileOpenRequester);

    constructor() {
        // Any component can ask to open a log file; we own the hidden input.
        this.openRequester.requests$.pipe(takeUntilDestroyed()).subscribe(() => this.onFileClick());

        // Side-effect injections — these services self-register on instantiation
        inject(GlobalSettingsProvider);
        inject(ThemeManager);
        const logFileProvider = inject(LogFileProvider);
        const nullDefProvider = inject(BrowserNullParamDefProvider);
        const paramKeyedTranslator = inject(ParamKeyedTranslator);
        const paramSystemDefProvider = inject(ParamSystemDefProvider);
        const translationManager = inject(TranslationManager);
        const platform = inject(Platform);

        addIcons({
            'chevron-down-outline': chevronDownOutline,
            documentOutline,
            layersOutline,
            settingsOutline,
            helpCircleOutline,
            add,
            refresh,
            removeCircle,
            close,
            pencilOutline,
            calculatorOutline,
            warning,
        });
        this.fileInputAccept = platform.is('ios') ? 'application/octet-stream' : '.hjl';
        paramSystemDefProvider.setProviders([logFileProvider, nullDefProvider]);
        translationManager.add(paramKeyedTranslator);
    }

    @HostListener('window:dragover', ['$event'])
    onDragover(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
    }

    @HostListener('window:dragenter', ['$event'])
    onDragEnter(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
    }

    @HostListener('window:drop', ['$event'])
    onDrop(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        if (event.dataTransfer) {
            this.onFileList(event.dataTransfer.files).catch((err) => this.logger.error(err));
        }
    }

    @HostListener('window:keydown', ['$event'])
    onKeydown(event: KeyboardEvent) {
        // Skip when the user is typing in an editable element
        const target = event.target as HTMLElement | null;
        const tag = target?.tagName?.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select' || target?.isContentEditable) {
            return;
        }
        // F1 = Help (no modifier)
        if (event.key === 'F1' && !event.metaKey && !event.ctrlKey && !event.altKey && !event.shiftKey) {
            event.preventDefault();
            this.openHelpPage();
            return;
        }
        // Plain Cmd/Ctrl shortcuts
        if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) {
            return;
        }
        const key = event.key.toLowerCase();
        if (key === 'o') {
            event.preventDefault();
            this.onFileClick();
        } else if (key === ',') {
            event.preventDefault();
            this.openSettingsModal();
        } else if (key === 's' && this.isLoaded()) {
            event.preventDefault();
            this.saveConfiguration();
        }
    }

    async onFileList(files: FileList | null) {
        if (!files || files.length === 0) {
            return;
        }
        if (files.length > 1) {
            const toast = await this.toastController.create({
                buttons: [{ role: 'cancel', text: 'OK' }],
                duration: 3000,
                message: 'Loading multiple files at once is not supported.',
                translucent: true,
            });
            await toast.present();
            return;
        }

        const loading = await this.loadingController.create({
            spinner: 'crescent',
            message: 'Please wait, loading…',
        });
        await loading.present();
        let file: LogFile;
        try {
            file = await this.logReader.read(files[0]);
            await this.logFileProviderInstance.load(file);
        } catch (err) {
            this.logger.error(err);
            await loading.dismiss();
            const message = safeErrorString(err);
            const toast = await this.toastController.create({
                buttons: [{ role: 'cancel', text: 'OK' }],
                header: `Error loading ${files[0].name} `,
                message,
                translucent: true,
            });
            await toast.present();
            return;
        }

        const dataResult = await firstValueFrom(
            toObservable(this.dataProvider.result, { injector: this.injector }).pipe(
                truthyOnly(),
                filter((r) => r.file === file),
            ),
        );
        await loading.dismiss();
        if (!dataResult?.paramSet) {
            this.logger.error(dataResult?.error);
            const message = safeErrorString(dataResult?.error);
            const toast = await this.toastController.create({
                buttons: [{ role: 'cancel', text: 'OK' }],
                header: `Error loading ${files[0].name} `,
                message,
                translucent: true,
            });
            await toast.present();
        }
        this.isLoading = false;
    }

    onFileClick() {
        this.fileInput().nativeElement.value = '';
        this.fileInput().nativeElement.click();
        this.menuCtrl.close();
    }

    onRestoreClick() {
        this.configFileInput().nativeElement.value = '';
        this.configFileInput().nativeElement.click();
        this.menuCtrl.close();
    }

    onFileInputChange(_: Event) {
        this.signalGroupProvider.setAppliedConfigurationFileName(undefined);
        this.onFileList(this.fileInput().nativeElement.files).catch((err) => this.logger.error(err));
    }

    async openSignalsModal() {
        await this.menuCtrl.close();
        this.navCtrl.navigateForward('/signals');
    }

    async openCalculatedSignalsPage() {
        await this.menuCtrl.close();
        this.navCtrl.navigateForward('/calculated-signals');
    }

    async openConfigurationsModal() {
        await this.menuCtrl.close();
        this.navCtrl.navigateForward('/configurations');
    }

    get hasConfigurations() {
        return this.chartConfigProvider.chartConfigurationNames().length > 0;
    }

    async loadConfiguration() {
        await this.menuCtrl.close();
        const configs = this.chartConfigProvider.chartConfigurationNames();
        if (configs.length === 0) return;

        const actionSheet = await this.actionSheetCtrl.create({
            header: 'Load Configuration',
            buttons: [
                ...configs.map((name) => ({
                    text: name,
                    handler: () => {
                        this.chartConfigProvider.loadConfiguration(name);
                    },
                })),
                {
                    text: 'Cancel',
                    icon: 'close',
                    role: 'cancel',
                },
            ],
        });
        await actionSheet.present();
    }

    async saveConfiguration() {
        await this.menuCtrl.close();

        const alert = await this.alertCtrlr.create({
            header: 'Save Configuration',
            message: 'Enter configuration name to save',
            inputs: [
                {
                    name: 'name',
                    type: 'text',
                    placeholder: 'Name',
                },
            ],
            buttons: [
                {
                    text: 'Cancel',
                    role: 'cancel',
                },
                {
                    text: 'Save',
                    handler: (data) => {
                        if (data.name && data.name.trim().length > 0) {
                            const name = data.name.trim();
                            const existingConfigs = this.chartConfigProvider.chartConfigurationNames();

                            if (existingConfigs.includes(name)) {
                                this.alertCtrlr
                                    .create({
                                        header: 'Overwrite Configuration',
                                        message: `A configuration named "${name}" already exists. Overwrite?`,
                                        buttons: [
                                            { text: 'Cancel', role: 'cancel' },
                                            {
                                                text: 'Overwrite',
                                                handler: () => this.chartConfigProvider.saveConfiguration(name),
                                            },
                                        ],
                                    })
                                    .then((a) => a.present());
                            } else {
                                this.chartConfigProvider.saveConfiguration(name);
                            }
                        }
                    },
                },
            ],
        });
        await alert.present();
    }

    async openSettingsModal() {
        await this.menuCtrl.close();
        this.navCtrl.navigateForward('/settings');
    }

    async openHelpPage() {
        await this.menuCtrl.close();
        this.navCtrl.navigateForward('/help');
    }

    async backupConfigurations() {
        await this.menuCtrl.close();
        const configs = this.chartConfigProvider.getAllConfigurations();
        if (!configs || Object.keys(configs).length === 0) return;

        const dataStr = JSON.stringify(configs, null, 2);
        const fileName = `hgm_charts_backup.json`;

        if (electronBridge?.dialog) {
            const { canceled, filePath } = await electronBridge.dialog.showSaveDialog({
                defaultPath: fileName,
                filters: [{ name: 'JSON', extensions: ['json'] }],
            });
            if (!canceled && filePath) {
                await this.fileIo.write(filePath, dataStr);
                const toast = await this.toastController.create({
                    message: `Backup saved to ${filePath}`,
                    duration: 3000,
                });
                await toast.present();
            }
        } else {
            const blob = new Blob([dataStr], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            a.click();
            URL.revokeObjectURL(url);
        }
    }

    restoreConfigurations(event: any) {
        this.menuCtrl.close();
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const content = e.target?.result as string;
                const config = JSON.parse(content);

                const keys = Object.keys(config);
                if (keys.length > 0) {
                    const alert = await this.alertCtrlr.create({
                        header: 'Restore Configurations',
                        message: `Found ${keys.length} configurations. Restore all? This will overwrite configurations with the same name.`,
                        buttons: [
                            { text: 'Cancel', role: 'cancel' },
                            {
                                text: 'Restore All',
                                handler: () => {
                                    for (const key of keys) {
                                        this.chartConfigProvider.addConfiguration(key, config[key]);
                                    }
                                    this.cdr.markForCheck();
                                },
                            },
                        ],
                    });
                    await alert.present();
                } else {
                    throw new Error('Invalid format');
                }
            } catch (err) {
                this.logger.error('Failed to parse JSON configuration backup', err);
                const errorAlert = await this.alertCtrlr.create({
                    header: 'Restore Failed',
                    message: 'The selected file is not a valid configurations backup.',
                    buttons: ['OK'],
                });
                await errorAlert.present();
                this.cdr.markForCheck();
            }
        };
        reader.readAsText(file);
        event.target.value = '';
    }

    get appVersion() {
        return packageInfo.version;
    }

    get appHash() {
        return gitHash;
    }

    get browserPlatformString(): string {
        try {
            const nav = navigator as any;
            if (nav.userAgentData) {
                const brands = nav.userAgentData.brands || [];
                // Filter out generic noise like 'Not:A-Brand' or internal 'Chromium' when possible
                const brand = brands.find((b: any) => !b.brand.includes('Not') && b.brand !== 'Chromium') || brands[0];
                const brandStr = brand ? `${brand.brand} ${brand.version}` : 'Browser';
                const platformStr = nav.userAgentData.platform || 'Unknown OS';
                return `${brandStr} / ${platformStr}`;
            }

            // Fallback for non-Chromium browsers (Firefox, Safari, older browsers)
            const ua = navigator.userAgent;
            let browser = 'Unknown Browser';
            let os = 'Unknown OS';

            if (ua.includes('Firefox')) browser = 'Firefox';
            else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
            else if (ua.includes('Edge')) browser = 'Edge';
            else if (ua.includes('Chrome')) browser = 'Chrome';

            if (ua.includes('Win')) os = 'Windows';
            else if (ua.includes('Mac')) os = 'macOS';
            else if (ua.includes('Linux')) os = 'Linux';
            else if (ua.includes('Android')) os = 'Android';
            else if (ua.includes('like Mac')) os = 'iOS';

            return `${browser} / ${os}`;
        } catch {
            return 'Web Browser';
        }
    }

    get updateAvailable() {
        const available = this.updateProvider.available();
        if (!available) return false;
        return available.latestVersion.hash !== available.currentVersion.hash;
    }
}
